import { useMemo } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Box } from '@givebutter/primitives/box';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal } from '@givebutter/util';
import {
    faArrowRight,
    faArrowUpRightFromSquare,
    faDollarSign,
    faEdit,
    faTrashAlt,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as plansApi from 'api/transactionPlans';
import * as DeletePlanDialog from 'assets/js/pages/account-settings/imported-recurring-plans/DeletePlanDialog';
import * as EditPlanDialog from 'assets/js/pages/account-settings/imported-recurring-plans/EditPlanDialog';
import * as Styled from 'assets/js/pages/account-settings/imports/styled';
import * as DropdownMenu from 'components/dropdown-menu';
import { useReactTableState } from 'components/table';
import * as Table from 'components/table';
import { IMPORT_PLANS_LINK } from 'constants/links';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { replace, upperFirst } from 'lodash';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { twMerge } from 'tailwind-merge';
import { TransactionPlan, TransactionPlanStatus } from 'types/domain';

const columnHelper = createColumnHelper<TransactionPlan>();

const RecurringPlans = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const { pagination, setPagination, expanded, setExpanded } = useReactTableState();
    const queryClient = useQueryClient();

    const { data: plans } = useSuspenseQuery({
        queryKey: accountQueryKeys.plansWithFilters(accountId, { page: pagination.pageIndex + 1 }),
        queryFn: () =>
            plansApi.fetchPlans(accountId, { page: pagination.pageIndex + 1, context: 'imports' }),
    });

    const { mutate: verifyPlans, ...verifyPlansMutation } = useMutation({
        mutationFn: () => plansApi.verifyPlans({ accountId }),
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.plans(accountId) }),
    });

    // Intentionally not re-using the same mutation as `activatePlans`
    // because we use its states for rendering and reusing it
    // would provide inaccurate feedback to the user.
    const { mutate: activatePlan } = useMutation({
        mutationFn: (planId: number) => plansApi.activatePlans({ accountId, planIds: [planId] }),
        onSuccess() {
            toast.success('Recurring plan activated.');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.plans(accountId) });
        },
    });

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'first_name',
                header: 'Name',
                cell: info => {
                    const plan = info.row.original;

                    return (
                        <p className={'font-medium'}>
                            {plan.first_name} {plan.last_name}
                        </p>
                    );
                },
            }),
            columnHelper.accessor('status', {
                header: 'Status',
                cell: info => (
                    <div className={'flex flex-nowrap items-center gap-x-2'}>
                        <div className={`h-2 w-2 rounded-full ${statusColor(info.getValue())}`} />
                        {formattedPlanStatus(info.getValue())}
                    </div>
                ),
            }),
            columnHelper.accessor('campaign', {
                header: 'Campaign',
                cell: info => {
                    return <div>{info.getValue().title}</div>;
                },
            }),
            columnHelper.accessor('next_bill_date', {
                id: 'next_bill_date',
                header: 'Next Bill Date',
                cell: info => {
                    const date = DateTime.fromISO(info.getValue(), { zone: 'UTC' }).toLocaleString(
                        DateTime.DATE_FULL,
                    );

                    return <div>{date}</div>;
                },
            }),
            columnHelper.accessor('amount', {
                id: 'amount',
                header: 'Amount',
                cell: info => {
                    return (
                        <div>
                            <CurrencySymbol />
                            {maskDecimal(info.getValue())}
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const plan = info.row.original;
                    const planIsReady = plan.status === 'ready';

                    return (
                        <DropdownMenu.Root key={plan.id}>
                            <DropdownMenu.EllipsisTrigger className="float-right px-2 py-1" />
                            <DropdownMenu.Content sideOffset={4} align={'end'}>
                                <DropdownMenu.Item
                                    disabled={!planIsReady}
                                    onClick={e => {
                                        e.preventDefault();
                                        if (planIsReady) {
                                            activatePlan(plan.id);
                                        }
                                    }}
                                    className={twMerge(
                                        'flex items-center gap-1.5',
                                        !planIsReady &&
                                            'pointer-events-none !cursor-not-allowed opacity-60 hover:!bg-transparent',
                                    )}
                                >
                                    <FontAwesomeIcon
                                        className={'text-gray-500'}
                                        icon={faDollarSign}
                                    />
                                    Activate Plan
                                </DropdownMenu.Item>
                                <DeletePlanDialog.Root plan={plan}>
                                    <DeletePlanDialog.Trigger type={'reset'} asChild={true}>
                                        <DropdownMenu.Item
                                            disabled={!['imported', 'ready'].includes(plan.status)}
                                            onSelect={e => e.preventDefault()}
                                            className={'flex items-center gap-1.5'}
                                        >
                                            <FontAwesomeIcon
                                                className={'text-gray-500'}
                                                icon={faTrashAlt}
                                            />
                                            Delete Plan
                                        </DropdownMenu.Item>
                                    </DeletePlanDialog.Trigger>
                                </DeletePlanDialog.Root>
                                <EditPlanDialog.Root plan={plan}>
                                    <EditPlanDialog.Trigger type={'reset'} asChild={true}>
                                        <DropdownMenu.Item
                                            disabled={!['imported', 'ready'].includes(plan.status)}
                                            onSelect={e => e.preventDefault()}
                                            className={'flex items-center gap-1.5'}
                                        >
                                            <FontAwesomeIcon
                                                className={'text-gray-500'}
                                                icon={faEdit}
                                            />
                                            Edit Plan
                                        </DropdownMenu.Item>
                                    </EditPlanDialog.Trigger>
                                </EditPlanDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [activatePlan],
    );

    const table = useReactTable({
        data: plans.data,
        columns,
        pageCount: plans?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            expanded,
        },
        onPaginationChange: setPagination,
        onExpandedChange: setExpanded,
        manualPagination: true,
        enableSorting: false,
    });

    return (
        <div className={'flex flex-col gap-y-4'}>
            {plans.data.length > 0 && (
                <Styled.Wrapper className={'mb-8 gap-x-8'}>
                    <Styled.Card>
                        <div className={'mb-2'}>
                            <Styled.CardHeader>Step 1: Transfer Tokens</Styled.CardHeader>
                            <Styled.CardBody>
                                Follow the instructions outlined in the link below to transfer your
                                Stripe tokens to Givebutter. This will allow Givebutter to charge
                                your supporters on your behalf.
                            </Styled.CardBody>
                        </div>
                        <Styled.CardLink
                            href={IMPORT_PLANS_LINK}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                            className={'flex items-center gap-x-2'}
                        >
                            View the step-by-step guide{' '}
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                        </Styled.CardLink>
                    </Styled.Card>

                    <Styled.Card className={'relative'}>
                        <div className={'mb-2'}>
                            <Styled.CardHeader>Step 2: Validate Stripe Tokens</Styled.CardHeader>
                            <Styled.CardBody>
                                Once your tokens have been transferred, attempt to validate them in
                                Givebutter by clicking the button below. The Status will change from
                                Pending to Ready if the token was successfully transferred.
                            </Styled.CardBody>
                        </div>
                        {verifyPlansMutation.isPending && (
                            <div>
                                <Loader />
                            </div>
                        )}
                        {verifyPlansMutation.isSuccess && (
                            <AlertNotification.Root type="success">
                                <AlertNotification.Content>
                                    <AlertNotification.Title>
                                        Validation in progress. We'll email you when complete.
                                    </AlertNotification.Title>
                                </AlertNotification.Content>
                            </AlertNotification.Root>
                        )}
                        {(verifyPlansMutation.isIdle || verifyPlansMutation.isError) && (
                            <Styled.CardAction
                                disabled={!hasValidateablePlans(plans.data)}
                                onClick={() => verifyPlans()}
                                className={`${
                                    !hasValidateablePlans(plans.data)
                                        ? 'pointer-events-none opacity-60 hover:!bg-transparent'
                                        : ''
                                }flex items-center gap-x-2`}
                            >
                                Validate Plans <FontAwesomeIcon icon={faArrowRight} />
                            </Styled.CardAction>
                        )}
                    </Styled.Card>
                    <Styled.Card className={'relative'}>
                        <div>
                            <Styled.CardHeader>Step 3: Activate Plans</Styled.CardHeader>
                            <Styled.CardBody>
                                Now that your plans have been validated, they are ready to be
                                activated and charged. Click the three dots on each plan below to
                                activate it.{' '}
                                <b>
                                    Make sure you have deactivated your old plans before doing so.
                                </b>
                            </Styled.CardBody>
                        </div>
                    </Styled.Card>
                </Styled.Wrapper>
            )}
            <Table.Root table={table} variant="bordered">
                <div className="flex flex-col gap-y-4">
                    <Box className="overflow-x-auto">
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant={'secondary'}>
                            <Table.EmptyIcon />
                            <Table.EmptyTitle>No plans to validate or activate.</Table.EmptyTitle>
                        </Table.Empty>
                    </Box>
                    <Table.Pagination>
                        <Table.PaginationPageNavigation />
                    </Table.Pagination>
                </div>
            </Table.Root>
        </div>
    );
};

const statusColor = (status: TransactionPlanStatus) =>
    ({
        imported: 'bg-blue-500',
        ready: 'bg-green-500',
        active: 'bg-gray-500',
        cancelled: 'bg-gray-500',
        past_due: 'bg-gray-500',
        past_due_failed: 'bg-gray-500',
        ended: 'bg-gray-500',
        paused: 'bg-gray-500',
        paused_temporarily: 'bg-gray-500',
    })[status];

const formattedPlanStatus = (status: TransactionPlanStatus) => {
    switch (status) {
        case 'imported':
            return 'Pending';
        default:
            return upperFirst(replace(status, '_', ' '));
    }
};

const hasValidateablePlans = (plans: TransactionPlan[]) =>
    plans.some(plan => plan.status === 'imported');

export { RecurringPlans };
